import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faRedo } from '@fortawesome/free-solid-svg-icons/faRedo';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons/faTrashAlt';
import { catchError, distinctUntilChanged, filter, mergeMap, skip, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { PatientCreationOverviewComponent } from '../patient-creation-overview/patient-creation-overview.component';
import { PatientCreationInstanceCreationComponent } from '../patient-creation-instance-creation/patient-creation-instance-creation.component';
import { InvitationInterface } from '../../../models/interface/invitation.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { UserActionTypes } from '../../../store/user/user.action';
import { AuthenticationStore } from '../../../store/authentication/component-store/authentication.store';
import { faList } from '@fortawesome/free-solid-svg-icons/faList';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

/**
 * Component:
 * Patient page displaying all patients of the eCoach;
 * Can be found: {uri}/patients
 */
@Component({
  selector: 'app-patient',
  templateUrl: './patient.component.html',
  styleUrls: ['./patient.component.scss'],
  providers: [AuthenticationStore],
})
export class PatientComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  @ViewChild('patientCreationOverview') patientCreationOverview: PatientCreationOverviewComponent;
  @ViewChild('assignInterventionContainer', { read: ViewContainerRef }) assignInterventionContainer: ViewContainerRef;

  @ViewChild('inviteStudyContainer', { read: ViewContainerRef }) inviteStudyContainer: ViewContainerRef;

  public patientCreationInstanceComponentClass = PatientCreationInstanceCreationComponent;

  // Icons
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faEnvelope = faEnvelope;
  faCheckCircle = faCheckCircle;
  faTimesCircle = faTimesCircle;
  faInfoCircle = faInfoCircle;
  faTrashAlt = faTrashAlt;
  faRedo = faRedo;
  faList = faList;

  // Filter
  public filter = { searchFilter: '', selectedSort: '', selectedVerified: 'all_verification_states' };

  public isLoading$: Observable<boolean>;
  public isCollapsed$: Observable<boolean>;

  // StudyForm - study invitation
  public studyForm: UntypedFormGroup;
  public searchTextSubject = new BehaviorSubject<string>('');

  public patients$: Observable<UserInterface[]>;
  public pagedPatients$: Observable<UserInterface[]>;

  public pendingInvitations: InvitationInterface[] = [];
  public verifiedSubject = new BehaviorSubject<boolean[]>(new Array(20).fill(false));

  public profile$: Observable<ProfileInterface>;

  public resendVerificationResponse$: Observable<any>;
  public isCollapsedSubject = new BehaviorSubject<boolean>(true);

  public sortSelection = ['email_ascending', 'email_descending'] as const;

  public filterVerified = ['all_verification_states', 'is_verified', 'is_unverified'] as const;

  private myPatients: UserInterface[] = [];
  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private patientsSubject = new BehaviorSubject<UserInterface[]>([]);
  private pagedPatientsSubject = new BehaviorSubject<UserInterface[]>([]);

  private myMembers$: Observable<UserInterface[]>;
  private allInvitations$: Observable<InvitationInterface[]>;
  private myRegisteredUsers$: Observable<UserInterface[]>;

  private subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private authenticationStore: AuthenticationStore,
    private store: Store<{
      myProfile: ProfileInterface;
      myMembers: UserInterface[];
      allInvitations: InvitationInterface[];
      myRegisteredUsers: UserInterface[];
    }>,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.isCollapsed$ = this.isCollapsedSubject.asObservable();
    this.patients$ = this.patientsSubject.asObservable();
    this.pagedPatients$ = this.pagedPatientsSubject.asObservable();
    this.profile$ = store.select('myProfile');

    this.studyForm = this.formBuilder.group({
      studyList: new UntypedFormArray([]),
    });

    this.resendVerificationResponse$ = this.authenticationStore.resendVerificationResponse$;
    this.myMembers$ = this.store.select('myMembers');
    this.allInvitations$ = this.store.select('allInvitations');
    this.myRegisteredUsers$ = this.store.select('myRegisteredUsers');
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    // Get user's id
    this.subscriptions.push(
      this.profile$
        .pipe(
          filter(user => user !== null),
          take(1),
        )
        .subscribe(() => this.applyUserFilter()),
    );
  }

  public applyUserFilter(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadUserList(true).subscribe(() => this.isLoadingSubject.next(false)),
    );
  }

  hasInvitations(patient: UserInterface) {
    return this.pendingInvitations.some((inv) => inv.attributes.email === patient.attributes.email);
  }

  public reloadUserList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
    this.store.dispatch({
      type: UserActionTypes.getAllInvitationsOfEcoachMembersType,
      payload: {},
    });
    return this.allInvitations$.pipe(
      skip(1),
      take(1),
      mergeMap((pendingInvitations) => {
        this.pendingInvitations = pendingInvitations;
        this.store.dispatch({
          type: UserActionTypes.getUsersRegisteredByEcoachType,
          payload: {},
        });
        return this.myRegisteredUsers$.pipe(skip(1), take(1));
      }),
      mergeMap((myPatients) => {
        this.myPatients = myPatients;
        this.store.dispatch({
          type: UserActionTypes.getMyMembersType,
          payload: {},
        });
        return this.myMembers$.pipe(skip(1), take(1));
      }),
      mergeMap((myMembers) => {
        this.myPatients = this.filterUsersBy(this.helper.setUniqueMembers(this.myPatients, myMembers));
        return this.search(this.filter.searchFilter).pipe(
          mergeMap(
            () => this.helper.setPagedContent(this.patientsSubject, this.pagedPatientsSubject, setFirst, pageIndex, pageSize),
          ),
        );
      }),
      catchError(() => {
        this.pendingInvitations = [];
        this.myPatients = [];
        return this.search(this.filter.searchFilter).pipe(
          mergeMap(
            () => this.helper.setPagedContent(this.patientsSubject, this.pagedPatientsSubject, setFirst, pageIndex, pageSize),
          ),
        );
      }),
    );
  }

  public updateList(): void {
    this.subscriptions.push(this.reloadUserList().subscribe());
  }

  public updateInvitations(): void {
    this.store.dispatch({
      type: UserActionTypes.getAllInvitationsOfEcoachMembersType,
      payload: {},
    });
    this.allInvitations$.pipe(skip(1), take(1)).subscribe((result) => {
      this.pendingInvitations = result;
    });
  }

  public updatePagedPatients(event: UserInterface[]) {
    if (event) {
      this.pagedPatientsSubject.next(event);
      const currentPageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
      this.verifiedSubject = new BehaviorSubject<boolean[]>(new Array(currentPageSize).fill(false));
    }
  }

  public reloadPage() {
    this.applyUserFilter();
  }

  public resetFilter(): void {
    this.filter.selectedSort = '';
    this.filter.selectedVerified = 'all_verification_states';
    this.filter.searchFilter = '';
    this.applyUserFilter();
  }

  private compareUsersByEmail(a: UserInterface, b: UserInterface) {
    const emailA = a.attributes.email || '';
    const emailB = b.attributes.email || '';
    return emailA.localeCompare(emailB);
  }

  public filterUsersBy(users: UserInterface[]): UserInterface[] {
    if (this.filter.selectedVerified === 'is_verified') {
      users = users.filter((user) => user.attributes.is_verified.toString() === '1');
    } else if (this.filter.selectedVerified === 'is_unverified') {
      users = users.filter((user) => user.attributes.is_verified.toString() === '0');
    }
    switch (this.filter.selectedSort) {
      case 'email_ascending': {
        users = users.sort(this.compareUsersByEmail);
        break;
      }
      case 'email_descending': {
        users = users.sort((a, b) => this.compareUsersByEmail(b, a));
        break;
      }
      default:
        break;
    }
    return users;
  }

  // Reads search value and runs searchFilter
  private search(name: string): Observable<boolean> {
    this.searchTextSubject.next(name);
    return this.searchTextSubject.pipe(
      distinctUntilChanged(),
      mergeMap(() => {
        this.patientsSubject.next(this.helper.filterUsersBy(this.myPatients, this.filter.searchFilter.toLowerCase().trim()));
        this.verifiedSubject = new BehaviorSubject<boolean[]>(new Array(this.paginator ? this.paginator.pageSize : 20).fill(false));
        return of(true);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
